/**
 * WaveformPlayer Component
 *
 * Professional-grade waveform player using wavesurfer.js with regions support.
 * Features waveform and spectrogram views with detection overlays.
 */

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Spinner, ButtonGroup, Button } from '@heroui/react';
import { AudioWaveform, Activity } from 'lucide-react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import SpectrogramPlugin from 'wavesurfer.js/dist/plugins/spectrogram';

import type { Detection } from '@/api';

type ViewMode = 'waveform' | 'spectrogram' | 'both';

export interface WaveformPlayerRef {
  play: () => void;
  pause: () => void;
  playPause: () => void;
  seekTo: (seconds: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (volume: number) => void;
  zoom: (pxPerSec: number) => void;
  getDuration: () => number;
  getCurrentTime: () => number;
  getZoom: () => number;
}

interface WaveformPlayerProps {
  audioUrl: string | null;
  detections: Detection[];
  getColorForType: (type: string) => string;
  onTimeUpdate?: (time: number) => void;
  onDurationChange?: (duration: number) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
  onReady?: () => void;
  onRegionClick?: (detection: Detection) => void;
  onRegionHover?: (detection: Detection | null) => void;
  activeDetection?: Detection | null;
  height?: number;
  showTimeline?: boolean;
  showViewToggle?: boolean;
}

export const WaveformPlayer = forwardRef<WaveformPlayerRef, WaveformPlayerProps>(
  function WaveformPlayer(
    {
      audioUrl,
      detections,
      getColorForType,
      onTimeUpdate,
      onDurationChange,
      onPlayingChange,
      onReady,
      onRegionClick,
      onRegionHover,
      activeDetection,
      height = 128,
      showTimeline = false,
      showViewToggle = true,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const spectrogramRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const regionsRef = useRef<RegionsPlugin | null>(null);
    const regionMapRef = useRef<Map<string, Detection>>(new Map());
    const currentZoomRef = useRef<number>(50);

    // Store callbacks in refs to avoid re-initializing wavesurfer when they change
    const callbacksRef = useRef({
      onTimeUpdate,
      onDurationChange,
      onPlayingChange,
      onReady,
      onRegionClick,
      onRegionHover,
    });
    callbacksRef.current = {
      onTimeUpdate,
      onDurationChange,
      onPlayingChange,
      onReady,
      onRegionClick,
      onRegionHover,
    };

    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('waveform');

    // Expose player methods via ref
    useImperativeHandle(
      ref,
      () => ({
        play: () => wavesurferRef.current?.play(),
        pause: () => wavesurferRef.current?.pause(),
        playPause: () => wavesurferRef.current?.playPause(),
        seekTo: (seconds: number) => {
          const ws = wavesurferRef.current;
          if (ws) {
            const duration = ws.getDuration();
            if (duration > 0) {
              ws.seekTo(seconds / duration);
            }
          }
        },
        setPlaybackRate: (rate: number) => {
          wavesurferRef.current?.setPlaybackRate(rate);
        },
        setVolume: (volume: number) => {
          wavesurferRef.current?.setVolume(volume);
        },
        zoom: (pxPerSec: number) => {
          currentZoomRef.current = pxPerSec;
          wavesurferRef.current?.zoom(pxPerSec);
        },
        getDuration: () => wavesurferRef.current?.getDuration() ?? 0,
        getCurrentTime: () => wavesurferRef.current?.getCurrentTime() ?? 0,
        getZoom: () => currentZoomRef.current,
      }),
      []
    );

    // Initialize wavesurfer
    useEffect(() => {
      if (!containerRef.current) return;

      const regions = RegionsPlugin.create();
      regionsRef.current = regions;

      const plugins: Array<ReturnType<typeof RegionsPlugin.create> | ReturnType<typeof TimelinePlugin.create> | ReturnType<typeof SpectrogramPlugin.create>> = [regions];

      if (showTimeline && timelineRef.current) {
        plugins.push(
          TimelinePlugin.create({
            container: timelineRef.current,
            timeInterval: 1,
            primaryLabelInterval: 5,
            style: {
              fontSize: '10px',
              color: '#71717a',
            },
          })
        );
      }

      // Add spectrogram plugin
      if (spectrogramRef.current) {
        // Generate 256-element colormap (professional dark blue to yellow gradient)
        const colorMap: number[][] = [];
        for (let i = 0; i < 256; i++) {
          const t = i / 255;
          let r, g, b;
          if (t < 0.25) {
            // Black to dark blue
            const s = t / 0.25;
            r = 0;
            g = 0;
            b = s * 0.5;
          } else if (t < 0.5) {
            // Dark blue to cyan
            const s = (t - 0.25) / 0.25;
            r = 0;
            g = s * 0.8;
            b = 0.5 + s * 0.3;
          } else if (t < 0.75) {
            // Cyan to yellow
            const s = (t - 0.5) / 0.25;
            r = s * 1.0;
            g = 0.8 + s * 0.2;
            b = 0.8 - s * 0.8;
          } else {
            // Yellow to red/orange
            const s = (t - 0.75) / 0.25;
            r = 1.0;
            g = 1.0 - s * 0.5;
            b = 0;
          }
          colorMap.push([r, g, b, 1]);
        }

        plugins.push(
          SpectrogramPlugin.create({
            container: spectrogramRef.current,
            labels: true,
            height: height,
            splitChannels: false,
            colorMap: colorMap as Array<[number, number, number, number]>,
          })
        );
      }

      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: '#e4e4e7', // zinc-200
        progressColor: '#3b82f6', // blue-500
        cursorColor: '#3b82f6',
        cursorWidth: 2,
        height,
        normalize: true,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        plugins,
      });

      wavesurferRef.current = ws;

      ws.on('play', () => callbacksRef.current.onPlayingChange?.(true));
      ws.on('pause', () => callbacksRef.current.onPlayingChange?.(false));
      ws.on('timeupdate', (time) => callbacksRef.current.onTimeUpdate?.(time));
      ws.on('decode', (duration) => callbacksRef.current.onDurationChange?.(duration));
      ws.on('ready', () => {
        setIsLoading(false);
        callbacksRef.current.onReady?.();
      });
      ws.on('error', (err) => {
        // Ignore abort errors (caused by React StrictMode or component unmount)
        if (err.name === 'AbortError' || err.message?.includes('aborted')) {
          return;
        }
        setIsLoading(false);
        setLoadError(err.message || 'Failed to load audio');
      });

      // Region click handling
      regions.on('region-clicked', (region: Region, e: MouseEvent) => {
        e.stopPropagation();
        const detection = regionMapRef.current.get(region.id);
        if (detection && callbacksRef.current.onRegionClick) {
          callbacksRef.current.onRegionClick(detection);
        }
      });

      // Region hover handling
      regions.on('region-in', (region: Region) => {
        const detection = regionMapRef.current.get(region.id);
        if (detection && callbacksRef.current.onRegionHover) {
          callbacksRef.current.onRegionHover(detection);
        }
      });

      regions.on('region-out', () => {
        callbacksRef.current.onRegionHover?.(null);
      });

      return () => {
        ws.destroy();
      };
    }, [height, showTimeline]);

    // Load audio when URL changes
    useEffect(() => {
      if (!audioUrl || !wavesurferRef.current) return;

      const ws = wavesurferRef.current;
      let isCancelled = false;

      setIsLoading(true);
      setLoadError(null);

      ws.load(audioUrl).catch((err) => {
        // Ignore abort errors (caused by React StrictMode double-mount or URL change)
        if (isCancelled || err.name === 'AbortError' || err.message?.includes('aborted')) {
          return;
        }
        setIsLoading(false);
        setLoadError(err.message || 'Failed to load audio');
      });

      return () => {
        isCancelled = true;
        // Stop any in-progress loading
        ws.stop();
      };
    }, [audioUrl]);

    // Helper to create region ID from detection
    const getRegionId = useCallback((detection: Detection, index: number) => {
      return `detection-${detection.start_ms}-${detection.end_ms}-${index}`;
    }, []);

    // Sync detections to regions
    useEffect(() => {
      if (!regionsRef.current || !wavesurferRef.current) return;

      // Clear existing regions and map
      regionsRef.current.clearRegions();
      regionMapRef.current.clear();

      // Add regions for each detection
      detections.forEach((detection, index) => {
        const color = getColorForType(detection.artifact_type);
        const regionId = getRegionId(detection, index);

        // Calculate opacity based on confidence (0.2 to 0.6)
        const opacityHex = Math.round((0.2 + detection.confidence * 0.4) * 255)
          .toString(16)
          .padStart(2, '0');

        regionsRef.current?.addRegion({
          id: regionId,
          start: detection.start_ms / 1000,
          end: detection.end_ms / 1000,
          color: `${color}${opacityHex}`,
          drag: false,
          resize: false,
        });

        // Store detection reference
        regionMapRef.current.set(regionId, detection);
      });
    }, [detections, getColorForType, getRegionId]);

    // Highlight active detection
    useEffect(() => {
      if (!regionsRef.current) return;

      const regions = regionsRef.current.getRegions();

      regions.forEach((region) => {
        const detection = regionMapRef.current.get(region.id);
        if (!detection) return;

        const color = getColorForType(detection.artifact_type);
        const isActive =
          activeDetection &&
          activeDetection.start_ms === detection.start_ms &&
          activeDetection.end_ms === detection.end_ms &&
          activeDetection.artifact_type === detection.artifact_type;

        // Active regions get full opacity, others get confidence-based
        const opacityHex = isActive
          ? 'cc' // ~80% opacity
          : Math.round((0.2 + detection.confidence * 0.4) * 255)
              .toString(16)
              .padStart(2, '0');

        region.setOptions({ color: `${color}${opacityHex}` });
      });
    }, [activeDetection, getColorForType]);

    return (
      <div className="relative">
        {/* View mode toggle */}
        {showViewToggle && (
          <div className="flex justify-end mb-2">
            <ButtonGroup size="sm" variant="flat">
              <Button
                isIconOnly
                color={viewMode === 'waveform' ? 'primary' : 'default'}
                onPress={() => setViewMode('waveform')}
                aria-label="Waveform view"
              >
                <AudioWaveform className="h-4 w-4" />
              </Button>
              <Button
                isIconOnly
                color={viewMode === 'spectrogram' ? 'primary' : 'default'}
                onPress={() => setViewMode('spectrogram')}
                aria-label="Spectrogram view"
              >
                <Activity className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                color={viewMode === 'both' ? 'primary' : 'default'}
                onPress={() => setViewMode('both')}
                aria-label="Both views"
              >
                Both
              </Button>
            </ButtonGroup>
          </div>
        )}

        {/* Timeline container */}
        {showTimeline && (
          <div ref={timelineRef} className="mb-1" />
        )}

        {/* Loading/Error overlay */}
        {(isLoading || loadError) && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-lg z-10"
            style={{ backgroundColor: loadError ? 'rgb(254 242 242)' : 'rgb(250 250 250)' }}
          >
            {isLoading && (
              <div className="flex flex-col items-center gap-2">
                <Spinner size="lg" />
                <span className="text-sm text-default-500">Loading audio...</span>
              </div>
            )}
            {loadError && (
              <span className="text-sm text-danger">{loadError}</span>
            )}
          </div>
        )}

        {/* Waveform container */}
        <div
          className={`relative transition-opacity ${isLoading || loadError ? 'opacity-0' : 'opacity-100'}`}
          style={{ display: viewMode === 'spectrogram' ? 'none' : 'block' }}
        >
          <div
            ref={containerRef}
            style={{ minHeight: height }}
          />
        </div>

        {/* Spectrogram container */}
        <div
          className={`relative transition-opacity ${isLoading || loadError ? 'opacity-0' : 'opacity-100'}`}
          style={{
            display: viewMode === 'waveform' ? 'none' : 'block',
            marginTop: viewMode === 'both' ? '8px' : 0,
          }}
        >
          <div
            ref={spectrogramRef}
            style={{ minHeight: height }}
          />
        </div>
      </div>
    );
  }
);
